// TokenExtendDialog.js - Make sure export is correct

import { Box, Button, Dialog, Typography, useTheme, Fade, LinearProgress } from "@mui/material";
import { useApplicationContext } from "../../context/ApplicationContext";
import WarningIcon from "@mui/icons-material/Warning";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Severity } from "../../constant/ApplicationVariables";
import sessionManager from "../../api/useAxios/SessionManager";
import dayjs from "dayjs";

const COUNTDOWN_DURATION = 600; // 10 minutes in seconds

// Circular countdown component - Memoized to prevent re-renders
const CircularCountdown = React.memo(({ seconds, maxSeconds = COUNTDOWN_DURATION }) => {
  const theme = useTheme();
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(100, (seconds / maxSeconds) * 100);
  const offset = circumference - (progress / 100) * circumference;
  
  const getColor = () => {
    const percentage = (seconds / maxSeconds) * 100;
    if (percentage <= 10) return theme.palette.error.main;
    if (percentage <= 30) return theme.palette.warning.main;
    return theme.palette.primary.main;
  };

  const formatTime = (totalSeconds) => {
    const safeSeconds = Math.max(0, Math.min(totalSeconds, maxSeconds));
    const mins = Math.floor(safeSeconds / 60);
    const secs = Math.floor(safeSeconds % 60);
    return {
      minutes: mins.toString().padStart(2, '0'),
      seconds: secs.toString().padStart(2, '0')
    };
  };

  const time = formatTime(seconds);

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ position: 'relative', width: 150, height: 150 }}>
        <svg width="150" height="150" viewBox="0 0 120 120">
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={theme.palette.mode === 'light' ? '#e0e0e0' : '#333'}
            strokeWidth="4"
          />
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={getColor()}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            sx={{
              transform: 'rotate(-90deg)',
              transformOrigin: '50% 50%',
              transition: 'stroke-dashoffset 0.3s ease-in-out',
            }}
          />
        </svg>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{
              color: getColor(),
              fontFamily: 'monospace',
              fontSize: '2rem',
              lineHeight: 1,
            }}
          >
            {time.minutes}
            <Typography component="span" sx={{ fontSize: '1.2rem', mx: 0.2 }}>:</Typography>
            {time.seconds}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            remaining
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

function TokenExtendDialog() {
  const { 
    showValidTillConfirmation, 
    setShowValidTillConfirmation, 
    handleSnackbar, 
    handleLogout 
  } = useApplicationContext();
  const theme = useTheme();

  const [remainingSeconds, setRemainingSeconds] = useState(COUNTDOWN_DURATION);
  const [loading, setLoading] = useState(false);
  const [isManualLogout, setIsManualLogout] = useState(false);
  const [crossTabMessage, setCrossTabMessage] = useState(null);
  
  const isExtendingRef = useRef(false);
  const tabId = useRef(null);
  const isLoggingOutRef = useRef(false);
  const countdownIntervalRef = useRef(null);
  const isMountedRef = useRef(true);
  const lastRemainingRef = useRef(COUNTDOWN_DURATION);
  const updateTimeoutRef = useRef(null);

  // Initialize
  useEffect(() => {
    tabId.current = sessionManager.getTabId();
    isMountedRef.current = true;
    
    const shouldShowDialog = sessionManager.isDialogVisible;
    if (shouldShowDialog && !showValidTillConfirmation) {
      const profileDetails = sessionManager.getProfileDetails();
      const token = localStorage.getItem("access-token");
      const now = dayjs();
      const expiry = dayjs(profileDetails?.validTill);
      const timeUntilExpiry = expiry.diff(now, 'seconds');
      
      if (token && profileDetails?.validTill && timeUntilExpiry > 0) {
        const remaining = Math.min(COUNTDOWN_DURATION, Math.max(0, timeUntilExpiry));
        if (remaining <= COUNTDOWN_DURATION) {
          setShowValidTillConfirmation(true);
          setRemainingSeconds(remaining);
          lastRemainingRef.current = remaining;
        } else {
          sessionManager.isDialogVisible = false;
          sessionManager.hideDialog();
        }
      } else {
        sessionManager.isDialogVisible = false;
        sessionManager.hideDialog();
        handleLogout();
      }
    }
    
    return () => {
      isMountedRef.current = false;
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
    };
  }, []);

  // Main countdown effect
  useEffect(() => {
    if (!showValidTillConfirmation) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }

    const updateRemainingTime = () => {
      if (!isMountedRef.current || !showValidTillConfirmation) return;
      
      const profileDetails = sessionManager.getProfileDetails();
      if (profileDetails?.validTill) {
        const now = dayjs();
        const expiry = dayjs(profileDetails.validTill);
        const timeUntilExpiry = expiry.diff(now, 'seconds');
        let remaining = Math.min(COUNTDOWN_DURATION, Math.max(0, timeUntilExpiry));
        
        if (remaining > COUNTDOWN_DURATION) {
          if (showValidTillConfirmation) {
            setShowValidTillConfirmation(false);
            sessionManager.hideDialog();
          }
          return;
        }
        
        const diff = Math.abs(remaining - lastRemainingRef.current);
        if (diff >= 1) {
          lastRemainingRef.current = remaining;
          setRemainingSeconds(remaining);
        } else if (remaining === 0) {
          setRemainingSeconds(0);
          lastRemainingRef.current = 0;
        }
        
        if (remaining <= 0 && !isManualLogout && !isLoggingOutRef.current) {
          console.log('Auto-logging out due to countdown completion');
          isLoggingOutRef.current = true;
          setTimeout(() => {
            handleAutoLogout();
          }, 500);
        }
      }
    };

    updateRemainingTime();

    countdownIntervalRef.current = setInterval(() => {
      updateRemainingTime();
    }, 1000);

    const handleVisibilityChange = () => {
      if (!document.hidden && showValidTillConfirmation) {
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
        updateTimeoutRef.current = setTimeout(() => {
          updateRemainingTime();
          updateTimeoutRef.current = null;
        }, 300);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [showValidTillConfirmation, isManualLogout]);

  // Session manager callbacks
  useEffect(() => {
    const originalOnCrossTabLogout = sessionManager.onCrossTabLogout;
    const originalOnCrossTabExtend = sessionManager.onCrossTabExtend;
    const originalOnShowDialog = sessionManager.onShowDialog;
    const originalOnHideDialog = sessionManager.onHideDialog;
    const originalOnTokenExpiring = sessionManager.onTokenExpiring;
    
    sessionManager.onShowDialog = () => {
      if (isMountedRef.current) {
        const profileDetails = sessionManager.getProfileDetails();
        if (profileDetails?.validTill) {
          const now = dayjs();
          const expiry = dayjs(profileDetails.validTill);
          const timeUntilExpiry = expiry.diff(now, 'seconds');
          const remaining = Math.min(COUNTDOWN_DURATION, Math.max(0, timeUntilExpiry));
          
          if (remaining <= COUNTDOWN_DURATION) {
            setShowValidTillConfirmation(true);
            setRemainingSeconds(remaining);
            lastRemainingRef.current = remaining;
          }
        }
      }
      if (originalOnShowDialog) originalOnShowDialog();
    };
    
    sessionManager.onHideDialog = () => {
      if (isMountedRef.current) {
        setShowValidTillConfirmation(false);
        setRemainingSeconds(COUNTDOWN_DURATION);
        lastRemainingRef.current = COUNTDOWN_DURATION;
      }
      if (originalOnHideDialog) originalOnHideDialog();
    };
    
    sessionManager.onTokenExpiring = (seconds) => {
      if (isMountedRef.current) {
        const remaining = Math.min(COUNTDOWN_DURATION, Math.max(0, Math.ceil(seconds)));
        if (Math.abs(remaining - lastRemainingRef.current) >= 1 || remaining === 0) {
          lastRemainingRef.current = remaining;
          setRemainingSeconds(remaining);
        }
      }
      if (originalOnTokenExpiring) originalOnTokenExpiring(seconds);
    };
    
    sessionManager.onCrossTabLogout = (data) => {
      if (isMountedRef.current) {
        setCrossTabMessage({
          type: 'logout',
          message: data?.message || 'You have been logged out from another tab'
        });
        setShowValidTillConfirmation(false);
        handleSnackbar("Logged out from another tab or window", Severity.warning);
      }
      
      setTimeout(() => {
        handleLogout();
      }, 500);
      
      if (originalOnCrossTabLogout) {
        originalOnCrossTabLogout(data);
      }
    };
    
    sessionManager.onCrossTabExtend = (extendData) => {
      if (isMountedRef.current) {
        setCrossTabMessage({
          type: 'extend',
          message: 'Session extended'
        });
        handleSnackbar("Session extended", Severity.info);
        
        if (showValidTillConfirmation) {
          setTimeout(() => {
            if (isMountedRef.current) {
              setShowValidTillConfirmation(false);
              setRemainingSeconds(COUNTDOWN_DURATION);
              lastRemainingRef.current = COUNTDOWN_DURATION;
            }
          }, 500);
        }
        
        setTimeout(() => {
          if (isMountedRef.current) {
            setCrossTabMessage(null);
          }
        }, 3000);
      }
      
      if (originalOnCrossTabExtend) {
        originalOnCrossTabExtend(extendData);
      }
    };
    
    return () => {
      sessionManager.onShowDialog = originalOnShowDialog;
      sessionManager.onHideDialog = originalOnHideDialog;
      sessionManager.onTokenExpiring = originalOnTokenExpiring;
      sessionManager.onCrossTabLogout = originalOnCrossTabLogout;
      sessionManager.onCrossTabExtend = originalOnCrossTabExtend;
    };
  }, [setShowValidTillConfirmation, handleLogout, handleSnackbar, showValidTillConfirmation]);

  const handleExtendSession = useCallback(async () => {
    if (loading || isExtendingRef.current) return;
    
    console.log(`Tab ${tabId.current}: Extending session...`);
    isExtendingRef.current = true;
    setLoading(true);
    
    try {
      const success = await sessionManager.extendSession();
      
      if (success && isMountedRef.current) {
        handleSnackbar("Session extended successfully", Severity.success);
        setShowValidTillConfirmation(false);
        setRemainingSeconds(COUNTDOWN_DURATION);
        lastRemainingRef.current = COUNTDOWN_DURATION;
      } else if (!success) {
        throw new Error("Failed to extend session");
      }
    } catch (error) {
      console.error("Error extending token:", error);
      if (isMountedRef.current) {
        handleSnackbar(error.message || "Failed to extend session", Severity.error);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
      isExtendingRef.current = false;
    }
  }, [loading, handleSnackbar, setShowValidTillConfirmation]);

  const handleAutoLogout = useCallback(() => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    
    if (isMountedRef.current) {
      setShowValidTillConfirmation(false);
      handleSnackbar("Session expired due to inactivity", Severity.info);
    }
    
    sessionManager.logout();
    handleLogout();
    
    setTimeout(() => {
      isLoggingOutRef.current = false;
    }, 1000);
  }, [handleLogout, handleSnackbar, setShowValidTillConfirmation]);

  const handleLogOff = useCallback(() => {
    console.log(`Tab ${tabId.current}: User clicked logout`);
    setIsManualLogout(true);
    
    if (isMountedRef.current) {
      setShowValidTillConfirmation(false);
      handleSnackbar("Logged off successfully", Severity.info);
    }
    
    sessionManager.logout();
    handleLogout();
  }, [handleLogout, handleSnackbar, setShowValidTillConfirmation]);

  const getRemainingTimeDisplay = () => {
    const safeSeconds = Math.max(0, Math.min(remainingSeconds, COUNTDOWN_DURATION));
    const mins = Math.floor(safeSeconds / 60);
    const secs = Math.floor(safeSeconds % 60);
    return `${mins} minute${mins !== 1 ? 's' : ''} and ${secs} second${secs !== 1 ? 's' : ''}`;
  };

  const getWarningColor = () => {
    const percentage = (remainingSeconds / COUNTDOWN_DURATION) * 100;
    if (percentage <= 10) return theme.palette.error.main;
    if (percentage <= 30) return theme.palette.warning.main;
    return theme.palette.primary.main;
  };

  const getWarningMessage = () => {
    const percentage = (remainingSeconds / COUNTDOWN_DURATION) * 100;
    if (percentage <= 10) return "Session expiring now!";
    if (percentage <= 30) return "Session expiring soon!";
    return "Inactive for 10 minutes";
  };

  if (!showValidTillConfirmation) {
    return null;
  }

  return (
    <Dialog
      open={showValidTillConfirmation}
      onClose={() => {}}
      disableEscapeKeyDown={true}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
          width: 420,
          maxWidth: '90%',
          animation: 'slideUp 0.25s cubic-bezier(0.34, 1.2, 0.64, 1)',
          '@keyframes slideUp': {
            '0%': { opacity: 0, transform: 'translateY(40px) scale(0.96)' },
            '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
          },
        }
      }}
    >
      <Box>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2.5,
          pb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <WarningIcon sx={{ color: getWarningColor(), fontSize: 24 }} />
            <Typography variant="h6" fontWeight={600}>
              Session Timeout Warning
            </Typography>
          </Box>
        </Box>

        {crossTabMessage && (
          <Box sx={{ 
            mx: 2, 
            mb: 2, 
            p: 1, 
            bgcolor: crossTabMessage.type === 'logout' ? 'error.main' : 'info.main',
            borderRadius: 1,
            textAlign: 'center'
          }}>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {crossTabMessage.message}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularCountdown seconds={remainingSeconds} maxSeconds={COUNTDOWN_DURATION} />
        </Box>

        <Box sx={{ textAlign: 'center', px: 3, pb: 2 }}>
          <Typography 
            variant="body1" 
            sx={{ 
              color: getWarningColor(), 
              fontWeight: 600,
              mb: 1
            }}
          >
            {getWarningMessage()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your session will expire in {getRemainingTimeDisplay()}
          </Typography>
        </Box>

        <LinearProgress 
          variant="determinate" 
          value={(remainingSeconds / COUNTDOWN_DURATION) * 100}
          sx={{
            height: 3,
            backgroundColor: theme.palette.grey[200],
            '& .MuiLinearProgress-bar': {
              backgroundColor: getWarningColor(),
              transition: 'transform 0.5s ease-in-out',
            }
          }}
        />

        <Box sx={{ p: 2.5, pt: 2 }}>
          <Button
            onClick={handleExtendSession}
            variant="contained"
            disabled={loading}
            fullWidth
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              mb: 1,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark
              },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.grey[400]
              }
            }}
          >
            {loading ? 'Extending...' : 'Extend Session'}
          </Button>
          
          <Button
            onClick={handleLogOff}
            variant="outlined"
            startIcon={<ExitToAppIcon />}
            fullWidth
            sx={{
              borderColor: theme.palette.error.main,
              color: theme.palette.error.main,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                borderColor: theme.palette.error.dark,
                backgroundColor: theme.palette.error.main + '10'
              }
            }}
          >
            Log out now
          </Button>
          
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1.5, color: 'text.secondary' }}>
            Session will extend automatically when you interact with the page
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
}

export default TokenExtendDialog;